import { Obj, IObj, parseCombinedId } from '../entity.js'
import { AnyObj } from './types.js'
import {
  EventInstanceObj,
  ErrorEventInstanceObj,
  EventObj,
  ErrorEventObj
} from './events.js'
import {
  ProcessInstanceObj,
  FunctionInstanceObj,
  FunctionObj,
  ServiceObj,
  ProcessObj
} from './runtime.js'
import { ItemObj } from './content.js'
import {
  GroupObj,
  OrganizationObj,
  PersonObj,
  ServiceAccountObj
} from './organizational.js'
import { SystemObj } from './system.js'
import { FolderObj, PageObj } from './content.js'
import { DiagramObj } from './diagram.js'
import { SecurityPolicyObj, PolicyRuleObj } from './security.js'

// --- TypeOf to Class Map and Factory ---
type AnyConstructor = new (data: Record<string, unknown>) => AnyObj

export const typeOfToClassMap: Record<AnyObj['typeOf'], AnyConstructor> = {
  Event: EventObj as unknown as AnyConstructor,
  EventInstance: EventInstanceObj as unknown as AnyConstructor,
  ErrorEvent: ErrorEventObj as unknown as AnyConstructor,
  ErrorEventInstance: ErrorEventInstanceObj as unknown as AnyConstructor,
  Process: ProcessObj as unknown as AnyConstructor,
  ProcessInstance: ProcessInstanceObj as unknown as AnyConstructor,
  FunctionInstance: FunctionInstanceObj as unknown as AnyConstructor,
  Function: FunctionObj as unknown as AnyConstructor,
  Service: ServiceObj as unknown as AnyConstructor,
  Item: ItemObj as unknown as AnyConstructor,
  Group: GroupObj as unknown as AnyConstructor,
  System: SystemObj as unknown as AnyConstructor,
  Folder: FolderObj as unknown as AnyConstructor,
  Page: PageObj as unknown as AnyConstructor,
  Diagram: DiagramObj as unknown as AnyConstructor,
  SecurityPolicy: SecurityPolicyObj as unknown as AnyConstructor,
  PolicyRule: PolicyRuleObj as unknown as AnyConstructor,
  Organization: OrganizationObj as unknown as AnyConstructor,
  Person: PersonObj as unknown as AnyConstructor,
  ServiceAccount: ServiceAccountObj as unknown as AnyConstructor
}

export function registerTypeOfClass<T extends string>(
  typeOf: T,
  cls: new (data: Record<string, unknown>) => Obj<T>
): void {
  ;(typeOfToClassMap as Record<string, AnyConstructor>)[typeOf] =
    cls as unknown as AnyConstructor
}

export function createObj(data: IObj | (Record<string, unknown> & { id: string })): AnyObj
export function createObj(
  data: IObj | (Record<string, unknown> & { key: string; typeOf: string })
): AnyObj
export function createObj(
  data: IObj | (Record<string, unknown> & { typeOf: string })
): AnyObj
export function createObj(
  data: IObj | Record<string, unknown>,
  typeOf: string,
  key: string
): AnyObj
export function createObj(
  data: IObj | Record<string, unknown>,
  typeOf?: string,
  key?: string
): AnyObj {
  if (
    data.typeOf &&
    typeof data.typeOf === 'string' &&
    data.key &&
    typeof data.key === 'string'
  ) {
    // If data already has typeOf and key, use them directly
  } else if (data.typeOf && typeof data.typeOf === 'string' && !data.key) {
    // If only typeOf is provided, key will be auto-generated by the Obj constructor
    // No need to set data.key here
  } else if (typeOf && typeof typeOf === 'string' && key && typeof key === 'string') {
    data.typeOf = typeOf
    data.key = key
  } else if (typeOf && typeof typeOf === 'string' && !key) {
    // If only typeOf is provided as parameter, key will be auto-generated
    data.typeOf = typeOf
  } else if (data.id && typeof data.id === 'string') {
    const { key, typeOf } = parseCombinedId(data.id)
    data.typeOf = typeOf
    data.key = key
  } else {
    throw new Error(
      'Either id, or typeOf (with optional key) must be provided as strings to create an object'
    )
  }

  const Cls = typeOfToClassMap[data.typeOf as AnyObj['typeOf']]
  if (!Cls) throw new Error(`Unknown typeOf: ${data.typeOf}`)
  return new Cls(data as Record<string, unknown>)
}
